<script setup>
import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
  key: (route) => route.fullPath,
});

const { t, locale } = useI18n();
const route = useRoute();

const pageQuery = gql`
  query GetPageData($routeId: String) {
    page(route_id: $routeId) {
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      name
    }
    news {
      name
      content
      publish_date {
        timestamp
      }
      seo_url_slug {
        cs
        en
      }
      image
      perex
    }
  }
`;

const { clients } = useApollo();

const { data, error } = await useAsyncData(`news-index-${locale.value}`, () =>
  clients.default.query({
    query: pageQuery,
    variables: { routeId: route.name },
    fetchPolicy: 'network-only',
  })
);

const newsData = computed(() => data.value?.data?.news || []);
const page = computed(() => data.value?.data?.page);

const breadcrumbs = computed(() => [
  {
    title: t('pages.about.index.breadcrumbs-title'),
    route: 'about',
  },
  { title: t('pages.news.breadcrumbs-title') },
]);

useHeadData({
  title: computed(() => page.value?.seo_meta_title || page.value?.name || t('pages.news.breadcrumbs-title') || null),
  description: computed(() => page.value?.seo_meta_description || null),
  keywords: computed(() => page.value?.seo_meta_keywords || null),
  image: computed(() => page.value?.image || page.value?.cropped_detail_image || null),
  url: route.fullPath,
});

const newsSearch = ref('');

const filteredNews = computed(() => {
  if (!newsData.value) return [];
  return newsData.value.filter((item) => {
    return filterSearch(item.name) || filterSearch(item.perex);
  });
});

const searchUnite = (text) => {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const filterSearch = (name) => {
  return name ? searchUnite(name).indexOf(searchUnite(newsSearch.value)) > -1 : null;
};
</script>

<template>
  <main class="main">
    <Breadcrumbs :items="breadcrumbs" />
    <h1 class="heading">{{ t('pages.news.title') }}</h1>

    <div class="mt-4">
      <label for="search" class="hidden">{{ t('pages.news.search') }}</label>
      <input id="search" type="text" class="input input--search" :placeholder="t('pages.news.search')" v-model="newsSearch" />
    </div>

    <div v-if="newsData.length" class="container-content">
      <Card
        v-for="(item, key) in filteredNews"
        :key="key"
        :link="
          localePath({
            name: 'about-news-slug',
            params: { slug: item.seo_url_slug[locale] },
          })
        "
        :heading="item.name"
        :subheading="item.publish_date ? useDateFormat(item.publish_date?.timestamp) : null"
        :image-src="useGetImageUrl(item.image, 303, 303)"
        vcenter
        class="mt-5"
      >
        <div class="text text--3 mt-2" v-if="item.perex">
          <div v-if="item.perex" v-html="item.perex" />
        </div>
      </Card>
    </div>
  </main>
</template>
